import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateBlockDirective } from '@ngx-translate/core';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';
import { Skeleton } from 'primeng/skeleton';
import { InputCalendarInterface } from '../../../types/interfaces/input.interface';

type CalendarValue = Date | Date[] | null;
type CalendarStyle = Record<string, string>;

@Component({
  selector: 'app-input-calendar',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    FloatLabel,
    DatePicker,
    Skeleton,
    TranslateBlockDirective,
  ],
  templateUrl: './calendar.component.html',
  styleUrls: [
    '../../styles/input-base.css',
    './calendar.component.css',
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputCalendarComponent),
      multi: true,
    },
  ],
})
export class InputCalendarComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input()
  public formData!: InputCalendarInterface;

  @Input()
  public isLoading: boolean = false;

  public value: CalendarValue = null;
  public disabled: boolean = false;

  public datePickerRootStyle: CalendarStyle = {};
  public datePickerInputStyle: CalendarStyle = {};
  public datePickerPanelStyle: CalendarStyle = {};

  private _themeObserver: MutationObserver | null = null;

  private onChange: (value: CalendarValue) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    private readonly _changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  public ngOnInit(): void {
    this.initCalendarTheme();
    this.observeThemeChanges();
  }

  public ngOnDestroy(): void {
    this._themeObserver?.disconnect();
  }

  public checkError(): boolean {
    if (!this.formData.formGroup) return false;

    const formControl = this.formData.formGroup.get(this.formData.id);
    const isTouched = formControl?.touched === true;

    return isTouched && formControl?.hasError('required') || false;
  }

  public writeValue(value: CalendarValue): void {
    this.value = value;
  }

  public registerOnChange(onChange: (value: CalendarValue) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  public onStandaloneValueChange(value: CalendarValue): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  public get selectionMode(): 'single' | 'multiple' | 'range' {
    return this.formData.selectionMode ?? 'single';
  }

  public get readonlyInput(): boolean {
    return this.formData.readonlyInput ?? true;
  }

  public get showIcon(): boolean {
    return this.formData.showIcon ?? true;
  }

  public get dateFormat(): string {
    return this.formData.dateFormat ?? 'dd.mm.yy';
  }

  private observeThemeChanges(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    this._themeObserver = new MutationObserver(() => {
      this.initCalendarTheme();
    });

    this._themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: [
        'data-theme',
      ],
    });
  }

  private initCalendarTheme(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    const bodyStyle: CSSStyleDeclaration = getComputedStyle(document.body);
    const rootStyle: CSSStyleDeclaration = getComputedStyle(document.documentElement);

    const backgroundColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--glass-bg',
        '--glass-bg-1',
        '--surface-card',
      ],
      '#ffffff',
    );

    const strongBackgroundColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--glass-bg-2',
        '--surface-ground',
      ],
      '#f8fafc',
    );

    const hoverBackgroundColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--glass-bg-3',
        '--surface-hover',
      ],
      '#e5e7eb',
    );

    const borderColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--glass-bg-4',
        '--glass-border',
        '--border-color',
      ],
      'rgba(17, 24, 39, 0.16)',
    );

    const textColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--text-color',
        '--text-primary',
        '--app-text-color',
      ],
      '#111827',
    );

    const mutedTextColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--text-secondary',
        '--text-muted',
      ],
      '#6b7280',
    );

    const primaryColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--primary-color',
        '--p-primary-color',
      ],
      '#3b82f6',
    );

    const primaryTextColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--primary-contrast-color',
        '--p-primary-contrast-color',
      ],
      '#ffffff',
    );

    this.datePickerRootStyle = {
      width: '100%',

      '--p-datepicker-dropdown-background': strongBackgroundColor,
      '--p-datepicker-dropdown-hover-background': hoverBackgroundColor,
      '--p-datepicker-dropdown-active-background': hoverBackgroundColor,
      '--p-datepicker-dropdown-border-color': borderColor,
      '--p-datepicker-dropdown-hover-border-color': primaryColor,
      '--p-datepicker-dropdown-active-border-color': primaryColor,
      '--p-datepicker-dropdown-color': textColor,
      '--p-datepicker-dropdown-hover-color': textColor,
      '--p-datepicker-dropdown-active-color': textColor,
      '--p-datepicker-input-icon-color': textColor,
    };

    this.datePickerInputStyle = {
      background: backgroundColor,
      color: textColor,
      borderColor,
    };

    this.datePickerPanelStyle = {
      background: backgroundColor,
      color: textColor,
      borderColor,

      '--p-datepicker-panel-background': backgroundColor,
      '--p-datepicker-panel-border-color': borderColor,
      '--p-datepicker-panel-color': textColor,

      '--p-datepicker-header-background': backgroundColor,
      '--p-datepicker-header-border-color': borderColor,
      '--p-datepicker-header-color': textColor,

      '--p-datepicker-select-month-color': textColor,
      '--p-datepicker-select-month-hover-background': hoverBackgroundColor,
      '--p-datepicker-select-month-hover-color': textColor,

      '--p-datepicker-select-year-color': textColor,
      '--p-datepicker-select-year-hover-background': hoverBackgroundColor,
      '--p-datepicker-select-year-hover-color': textColor,

      '--p-datepicker-week-day-color': mutedTextColor,

      '--p-datepicker-date-color': textColor,
      '--p-datepicker-date-hover-background': hoverBackgroundColor,
      '--p-datepicker-date-hover-color': textColor,

      '--p-datepicker-date-selected-background': primaryColor,
      '--p-datepicker-date-selected-color': primaryTextColor,

      '--p-datepicker-date-range-selected-color': textColor,

      '--p-datepicker-today-color': textColor,

      '--p-datepicker-buttonbar-border-color': borderColor,
      '--p-datepicker-time-picker-border-color': borderColor,
    };

    this._changeDetectorRef.markForCheck();
  }

  private getCssVariable(
    bodyStyle: CSSStyleDeclaration,
    rootStyle: CSSStyleDeclaration,
    variableNames: string[],
    fallback: string,
  ): string {
    for (const variableName of variableNames) {
      const bodyValue: string = bodyStyle.getPropertyValue(variableName)
        .trim();

      if (bodyValue !== '') {
        return bodyValue;
      }

      const rootValue: string = rootStyle.getPropertyValue(variableName)
        .trim();

      if (rootValue !== '') {
        return rootValue;
      }
    }

    return fallback;
  }
}
